import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { toast } from "react-toastify";
import { FiGift, FiCheck, FiX, FiRefreshCw, FiClock, FiDollarSign } from "react-icons/fi";
import {
    Button,
    Card,
    PageHeader,
    StatCard,
    StatusBadge,
    DataTable,
    EmptyState,
    Tooltip,
    TableSkeleton,
    useConfirm,
    formatDateTime,
    formatRupiah,
} from "./ui";

export default function Penarikan() {
  const confirm = useConfirm();

  const [penarikanList, setPenarikanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPenarikan();

    const subscription = supabase
      .channel("public:penarikan")
      .on("postgres_changes", { event: "*", schema: "public", table: "penarikan" }, () =>
        fetchPenarikan()
      )
      .subscribe();

    return () => supabase.removeChannel(subscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPenarikan = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("penarikan")
      .select(`
        id,
        user_id,
        jumlah,
        status,
        created_at,
        referal (
          nama_lengkap,
          dana_number
        )
      `)
      .order("created_at", { ascending: false });

    if (error) {
      setError(error.message);
      setPenarikanList([]);
    } else {
      setPenarikanList(data);
      setError(null);
    }
    setLoading(false);
  };

  const updateStatus = async (id, status, row) => {
    const ok = await confirm({
      title: status === "disetujui" ? "Setujui Penarikan" : "Tolak Penarikan",
      message:
        status === "disetujui"
          ? `Setujui penarikan ${formatRupiah(row.jumlah)} untuk ${row.referal?.nama_lengkap || row.user_id}?`
          : `Tolak penarikan ${formatRupiah(row.jumlah)}?`,
      confirmLabel: status === "disetujui" ? "Setujui" : "Tolak",
      tone: status === "disetujui" ? "primary" : "danger",
    });
    if (!ok) return;

    const { error } = await supabase
      .from("penarikan")
      .update({ status })
      .eq("id", id);

    if (error) {
      toast.error("Gagal update status: " + error.message);
    } else {
      toast.success(`Status penarikan diperbarui menjadi '${status}'`);

      if (status === "disetujui") {
        fetch("/refapi/approve_penarikan", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: row.user_id, jumlah: row.jumlah })
        });
      }

      fetchPenarikan();
    }
  };

  const counts = {
    pending: penarikanList.filter((w) => w.status === "pending").length,
    approved: penarikanList.filter((w) => w.status === "disetujui").length,
    rejected: penarikanList.filter((w) => w.status === "ditolak").length,
    totalAmount: penarikanList
      .filter((w) => w.status === "disetujui")
      .reduce((sum, w) => sum + (w.jumlah || 0), 0),
  };

  const columns = [
    {
      key: "nama",
      header: "Nama",
      cell: (r) => (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {r.referal?.nama_lengkap || r.user_id}
        </span>
      ),
    },
    {
      key: "dana",
      header: "No. DANA",
      hideBelow: "lg",
      cell: (r) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{r.referal?.dana_number || "-"}</span>,
    },
    {
      key: "jumlah",
      header: "Jumlah",
      cell: (r) => <span className="font-semibold text-slate-800 dark:text-slate-200">{formatRupiah(r.jumlah)}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge status={r.status} size="sm" />,
    },
    {
      key: "created_at",
      header: "Tanggal",
      hideBelow: "lg",
      cell: (r) => <span className="text-sm text-slate-500 dark:text-slate-400">{formatDateTime(r.created_at)}</span>,
    },
    {
      key: "aksi",
      header: "Aksi",
      headerClassName: "text-right",
      className: "text-right",
      cell: (r) =>
        r.status === "pending" ? (
          <div className="flex items-center justify-end gap-1.5">
            <Button variant="success" size="sm" icon={FiCheck} onClick={() => updateStatus(r.id, "disetujui", r)}>
              Setujui
            </Button>
            <Button variant="secondary" size="sm" icon={FiX} onClick={() => updateStatus(r.id, "ditolak", r)}>
              Tolak
            </Button>
          </div>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">Selesai</span>
        ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Penarikan"
        subtitle="Kelola permintaan penarikan dana referal"
        icon={FiGift}
        actions={
          <Tooltip label="Muat ulang">
            <Button variant="secondary" size="icon" onClick={fetchPenarikan} aria-label="Muat ulang">
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </Tooltip>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Pending Withdrawal" value={counts.pending} icon={FiClock} tone="warning" />
        <StatCard label="Approved" value={counts.approved} icon={FiCheck} tone="success" />
        <StatCard label="Rejected" value={counts.rejected} icon={FiX} tone="danger" />
        <StatCard label="Total Amount" value={formatRupiah(counts.totalAmount)} icon={FiDollarSign} tone="primary" />
      </div>

      <Card>
        {loading ? (
          <TableSkeleton rows={8} columns={5} />
        ) : error ? (
          <div className="m-4 rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
            {error}
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={penarikanList}
            rowKey={(r) => r.id}
            empty={<EmptyState icon={FiGift} title="Tidak ada permintaan penarikan" description="Permintaan penarikan dari referal akan tampil di sini." />}
          />
        )}
      </Card>
    </div>
  );
}
